import {Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges} from '@angular/core';
import {DomSanitizer, SafeUrl} from '@angular/platform-browser';
import {categories, provinces} from "../../../types/custom-types";
import {FormBuilder, FormControl, FormGroup, Validators} from "@angular/forms";
import {FilePondFile, FileStatus} from "filepond";
import {CreateEventRequest} from "../../../model/request/create-event-request.model";
import {EventFormValidationUtil} from "../../../utils/event-form-validation.util";
import {EventResponse} from "../../../model/response/event/event-response.model";
import {AuthenticationService} from "../../../../service/authentication-service";
import {PrivilegeService} from "../../../../service/privilege.service";
import {MessageToastService} from "../../../../service/message-toast.service";

@Component({
    selector: 'app-event-form',
    templateUrl: './event-form.component.html',
    standalone: false
})
export class EventFormComponent implements OnInit, OnChanges, OnDestroy {

  @Input('apiErrors') apiErrors: Map<string, string> = new Map<string, string>()

  _event: EventResponse

  @Input('event')
  set setEvent(event: EventResponse) {
    this._event = event
    this.initForm()
  }

  @Input('formStyle') formStyle: string = ''
  @Output('onSubmitEvent') onSubmitEventEmiter = new EventEmitter<{
    data: CreateEventRequest,
    image: FilePondFile,
    original?: File,
    redacted?: File
  }>()

  readonly provinces: { code: string, description: string }[] = provinces()
  readonly categories: { code: string, description: string }[] = categories()

  isEditMode: boolean
  isAdmin: boolean

  eventMaxDate: Date = new Date()
  category: string
  stepIndex = 0
  imageStepOpened = false

  readonly steps = [
    {label: 'Intenção'},
    {label: 'Detalhes'},
    {label: 'Imagem'},
  ]

  readonly categoryCards = [
    {code: 'PEOPLE', src: 'category-people-v2.png', title: 'Pessoas', description: 'Crianças, adultos e idosos. Para quem se perdeu e para quem procura alguém.'},
    {code: 'VEHICLE', src: 'category-vehicle-v3.png', title: 'Veículos', description: 'Carros, motos, bicicletas e outros transportes, perdidos ou encontrados.'},
    {code: 'DOCUMENT', src: 'category-document-v2.png', title: 'Documentos', description: 'Bilhete de identidade, passaporte, carta de condução e outros papéis.'},
    {code: 'OTHER', src: 'category-other-v3.png', title: 'Diversos', description: 'Chaves, telemóveis, óculos, carteiras e o que não cabe nas outras categorias.'},
  ]

  eventForm: FormGroup

  protected eventImage: FilePondFile;
  redactOpen = false
  redactFile: Blob | null = null
  redactedFile: File | null = null
  redactedPreview: SafeUrl | null = null
  private originalFile: File | null = null
  private redactedPreviewUrl: string | null = null

  constructor(private fb: FormBuilder,
              private authService: AuthenticationService,
              private privilegeService: PrivilegeService,
              private messageToastService: MessageToastService,
              private sanitizer: DomSanitizer
  ) {
  }

  ngOnInit(): void {

    this.isAdmin = this.authService.isPrivilegedUser()

    this.isEditMode = this._event != null

    if (this.isEditMode) {
      this.category = this._event.category.code
    }

    this.initForm()
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['apiErrors'] || this.isEditMode || !this.apiErrors?.size) {
      return
    }
    if (this.apiErrors.has('intent') || this.apiErrors.has('category')) {
      this.stepIndex = 0
      return
    }
    if (this.apiErrors.has('eventImage')) {
      this.stepIndex = 2
      return
    }
    this.stepIndex = 1
  }

  initForm() {
    this.eventForm = this.fb.group(
      //general information control
      {
        "category": new FormControl(this._event?.category.code, [Validators.required]),
        "intent": new FormControl(this._event?.intent?.code ?? (this._event ? 'SEEKING' : null), [Validators.required]),
        "title": new FormControl(this._event?.title, [Validators.required]),
        "isContactPublic": new FormControl(this._event?.contactPublic),
        "isExplicitImage": new FormControl(this._event?.explicitImage),
        "contact": new FormControl(this._event?.contact?.split(','), [Validators.required]),
        "description": new FormControl(this._event?.description, [Validators.required]),
        "province": new FormControl(this._event?.province.code, [Validators.required]),
        "eventDate": new FormControl(this._event?.eventDate ? new Date(this._event.eventDate) : null, [Validators.required]),
        "reward": new FormControl(this._event?.reward),
        "identification": new FormControl(this._event?.identification),
        //
        "names": new FormControl(this._event?.personFilter?.names.split(','), [Validators.required]),
        "gender": new FormControl(this._event?.personFilter?.gender, [Validators.required]),
        "birthDate": new FormControl(this._event?.personFilter?.birthDate ? new Date(this._event.personFilter.birthDate) : null, [Validators.required]),
        //
        "brand": new FormControl(this._event?.vehicleFilter?.brand, [Validators.required]),
        "model": new FormControl(this._event?.vehicleFilter?.model, [Validators.required]),
        "licensePlate": new FormControl(this._event?.vehicleFilter?.licensePlate, [Validators.required]),
        "chassisNumber": new FormControl(this._event?.vehicleFilter?.chassiNumber),
        //
        "documentType": new FormControl(this._event?.documentFilter?.type?.code, [Validators.required]),
        "documentEntity": new FormControl(this._event?.documentFilter?.entity),
        //
        "termsConditions": new FormControl(false),

      }
    )
  }

  submit() {
    if (!this.isEditMode && this.stepIndex < 2) {
      this.nextStep()
      return
    }
    if (this.apiErrors != null) this.apiErrors.clear();
    this.displayFrontendErrors();

    if (this.apiErrors.size == 0) {
      this.onSubmitEventEmiter.emit({
        data: this.eventForm.value,
        image: this.eventImage,
        original: this.originalFile ?? undefined,
        redacted: this.redactedFile ?? undefined
      })
    } else {
      this.messageToastService.error("Existem erros no formulário")
    }
  }

  displayFrontendErrors() {
    this.apiErrors = EventFormValidationUtil.displayFrontEndFormErrors(this.eventForm, this.category)
    this.hasImageErrors()
  }

  hasImageErrors() {
    if (this.eventImage && this.eventImage.status != FileStatus.IDLE) {
      this.apiErrors.set("eventImage", "Porfavor verique o anexo")
    }
  }

  /**
   * changes the selected category
   * @param selected category selected
   */
  selectCategory(selected: string) {
    const intent = this.eventForm.get('intent')?.value
    this.eventForm.reset()
    this.apiErrors.clear()

    this.category = selected;
    this.eventForm.get('category').setValue(selected)
    this.eventForm.get('intent').setValue(intent)
  }

  selectExplicitImage(explicit: boolean) {
    this.eventForm.get('isExplicitImage').setValue(explicit)
  }

  selectIntent(intent: string) {
    this.eventForm.get('intent').setValue(intent)
    this.apiErrors?.delete('intent')
  }

  nextStep() {
    if (this.stepIndex === 0) {
      if (!this.eventForm.get('intent')?.value) {
        this.apiErrors.set('intent', 'Escolha se a ocorrência está à procura ou foi encontrada.')
      }
      if (!this.eventForm.get('category')?.value) {
        this.apiErrors.set('category', 'Escolha a categoria da ocorrência.')
      }
      if (!this.eventForm.get('intent')?.value || !this.eventForm.get('category')?.value) {
        return
      }
      this.stepIndex = 1
      return
    }

    if (this.stepIndex === 1) {
      this.apiErrors = EventFormValidationUtil.displayFrontEndFormErrors(this.eventForm, this.category)
      if (this.apiErrors.size > 0) {
        this.messageToastService.error("Existem erros no formulário")
        return
      }
      this.imageStepOpened = true
      this.stepIndex = 2
    }
  }

  previousStep() {
    if (this.stepIndex > 0) {
      this.stepIndex = this.stepIndex - 1
    }
  }

  goToStep(index: number) {
    if (index < this.stepIndex) {
      this.stepIndex = index
      return
    }
    if (index === this.stepIndex + 1) {
      this.nextStep()
    }
  }

  onFileUpdate($event: FilePondFile[]) {
    const next = $event[0]
    const changed = next?.id !== this.eventImage?.id || next?.file !== this.eventImage?.file
    this.eventImage = next
    if (!next?.file) {
      this.clearRedacted()
      this.redactOpen = false
      return
    }
    if (changed) {
      this.clearRedacted()
      this.redactFile = next.file
      this.redactOpen = true
    }
  }

  ngOnDestroy(): void {
    this.revokeRedactedPreview()
  }

  openRedact() {
    if (this.redactFile) {
      this.redactOpen = true
    }
  }

  onImageRedacted(result: {original: File, redacted: File}) {
    this.revokeRedactedPreview()
    this.originalFile = result.original
    this.redactedFile = result.redacted
    this.redactFile = result.original
    this.redactOpen = false
    this.redactedPreviewUrl = URL.createObjectURL(result.redacted)
    this.redactedPreview = this.sanitizer.bypassSecurityTrustUrl(this.redactedPreviewUrl)
  }

  private clearRedacted() {
    this.redactFile = null
    this.redactedFile = null
    this.originalFile = null
    this.revokeRedactedPreview()
  }

  private revokeRedactedPreview() {
    if (this.redactedPreviewUrl) {
      URL.revokeObjectURL(this.redactedPreviewUrl)
      this.redactedPreviewUrl = null
    }
    this.redactedPreview = null
  }

  onRedactClosed() {
    this.redactOpen = false
  }
}
